import { useEffect, useState } from 'react'
import { useLineSummaryStore } from 'store'
import { LineSummary } from 'types/api/loan'
import { trackEvent } from 'analytics/trackFunctions'

// components
import Card from 'components/Card'
import CreditLinesModal from 'components/LineModals/CreditLinesModal'

// assets
import { HelpIcon } from 'assets/icons'

// calls
import { CREDIT_PRODUCT, getCreditLinesSummary } from 'api/advances'

import { toLocaleCurrency } from 'utils/dinero'
import Skeleton from 'react-loading-skeleton'
import Meter from './Meter'
import Buttons from './Buttons'

const { WALLET, LOANS } = CREDIT_PRODUCT

interface OngoingAdvanceProps {
  className?: string
}

const OngoingAdvance = ({ className = '' }: OngoingAdvanceProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [advanceSummary, setAdvanceSummary] = useState<LineSummary>(null)
  const [loanSummary, setLoanSummary] = useState<LineSummary>(null)
  const [generalError, setGeneralError] = useState<boolean>(false)
  const [loading, setLoading] = useState(true)

  const { updateLineSummary } = useLineSummaryStore()

  useEffect(() => {
    Promise.all([getCreditLinesSummary(WALLET), getCreditLinesSummary(LOANS)])
      .then((res: LineSummary[]) => {
        const [advanceSummary, loanSummary] = res

        setAdvanceSummary(advanceSummary)
        setLoanSummary(loanSummary)

        updateLineSummary({
          interestFreeIsDepleted: advanceSummary.interestFreeIsDepleted,
          advanceLineIsDepleted: advanceSummary.isDepleted,
          loanLineIsDepleted: loanSummary.isDepleted
        })
      })
      .catch(() => setGeneralError(true))
      .finally(() => setLoading(false))
  }, [])

  if (loading) {
    return (
      <div className='w-full'>
        <Skeleton className='w-full rounded-lg mb-8' height={270} />
      </div>
    )
  }

  const renderText = () => {
    const bothLinesAreValid = loanSummary.isValid && advanceSummary.isValid
    const bothLinesAreInvalid = !loanSummary.isValid && !advanceSummary.isValid

    const sumAvailableAmount = () => {
      let sum = 0

      if (loanSummary.isValid) {
        sum = loanSummary.remainingAmount
      }

      if (advanceSummary.isValid) {
        sum += advanceSummary.remainingAmount
      }

      return sum
    }

    if (advanceSummary.isDepleted && loanSummary.isDepleted) {
      return 'Agotaste tus líneas de crédito'
    }

    if (bothLinesAreValid && loanSummary.isZero && advanceSummary.isZero) {
      return 'Sin oferta disponible'
    }

    if (
      bothLinesAreInvalid ||
      (!advanceSummary.isValid &&
        (loanSummary.isZero || loanSummary.remainingAmount === 0)) ||
      (!loanSummary.isValid &&
        (advanceSummary.isZero || advanceSummary.remainingAmount === 0))
    ) {
      return 'Averiguá cuánto podemos prestarte'
    }

    return `Tenés disponible: $${toLocaleCurrency(sumAvailableAmount())}`
  }

  return (
    <>
      <Card
        className={`bg-white text-black !outline-none w-full p-4 rounded-lg mt-4 relative ${className}`}
      >
        <div className='flex justify-between items-center'>
          <h2 className='font-bold text-xs'>Líneas de crédito</h2>
          <button
            onClick={() => {
              trackEvent({ eventName: 'bille_boton_info_lineas_de_credito' })
              setIsModalOpen(true)
            }}
            type='button'
          >
            <HelpIcon
              variant='GREEN'
              className='fill-green-800 h-[1.375rem] w-[1.375rem]'
            />
            <span className='sr-only'>Ver más</span>
          </button>
        </div>
        <hr className='my-4' />
        <Meter
          advanceAmount={advanceSummary.remainingAmount}
          loanAmount={loanSummary.remainingAmount}
          loanIsDepleted={loanSummary.isDepleted}
          advanceIsDepleted={advanceSummary.isDepleted}
          advanceIsValid={advanceSummary.isValid}
          loanIsValid={loanSummary.isValid}
        />
        <p className='text-center my-4 text-xs font-extrabold text-purple-900'>
          {renderText()}
        </p>
        <Buttons
          setAdvanceSummary={setAdvanceSummary}
          setLoanSummary={setLoanSummary}
          advanceSummary={advanceSummary}
          loanSummary={loanSummary}
        />
      </Card>
      <CreditLinesModal
        isShown={isModalOpen}
        setModalFunction={setIsModalOpen}
      />
    </>
  )
}

export default OngoingAdvance
